import type { ReactNode } from 'react';
import { BaseLedgerRow } from '../internal/BaseLedgerRow';
import { formatLedgerWhen, formatSitePlot } from '../format';
import { Money, StatusDot } from '../primitives';
import {
  composeRowAriaLabel,
  isCancelledStatus,
  statusToken,
  statusWord,
} from '../statusTokens';
import type { LedgerCollapse, LedgerColumn, LedgerRowMenu } from '../types';
import { composeMetaLine } from './metaLine';
import { workCells, type SitePlot } from './workCells';

export interface VariationLedgerRowProps {
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  /**
   * The work type's display name ("Day work", "Plastering", …). It is
   * org-configurable data, so the caller supplies it; the ledger never
   * assumes a work type exists or what it is called.
   */
  workTypeName: string;
  /**
   * The free-text entry. Demoted off the title line — it can be arbitrarily
   * long — and reappears at the tail of the meta line and in the hover card.
   */
  description?: string;
  /** A `WorkStatus` where the screen has one, any status string otherwise. */
  status: string;
  /**
   * Ref, who and when all come from the variation's allocation. A variation
   * with no allocations has none of them, and those cells render blank.
   */
  reference?: string | null;
  bookedBy?: string | null;
  /** ISO timestamp. */
  bookedAt?: string | null;
  /** Null when the viewer may not see money; undefined while `pending`. */
  amount?: number | null;
  sitePlot?: SitePlot;
  showValues?: boolean;
  /** The allocation behind the row has not arrived: its cells show placeholders. */
  pending?: boolean;
  hoverCard?: ReactNode;
  menu?: LedgerRowMenu;
  onClick?: () => void;
  'data-testid'?: string;
}

/** A variation row, titled by its work type rather than its description. */
export const VariationLedgerRow = ({
  columns,
  collapse,
  workTypeName,
  description,
  status,
  reference,
  bookedBy,
  bookedAt,
  amount,
  sitePlot,
  showValues = true,
  pending = false,
  hoverCard,
  menu,
  onClick,
  'data-testid': dataTestId,
}: VariationLedgerRowProps) => {
  const cancelled = isCancelledStatus(status);
  const meta = composeMetaLine([
    reference,
    statusWord(status),
    bookedBy,
    formatLedgerWhen(bookedAt),
    sitePlot ? formatSitePlot(sitePlot.site, sitePlot.plot) : null,
    description,
  ]);

  return (
    <BaseLedgerRow
      columns={columns}
      collapse={collapse}
      lead={<StatusDot color={statusToken(status).color} />}
      title={workTypeName}
      cells={workCells({ reference, bookedBy, bookedAt, sitePlot })}
      value={
        pending && amount === undefined ? undefined : (
          <Money value={amount} strike={cancelled} />
        )
      }
      meta={meta}
      dimmed={cancelled}
      showValues={showValues}
      pending={pending}
      hoverCard={hoverCard}
      menu={menu}
      onClick={onClick}
      aria-label={composeRowAriaLabel(workTypeName, status, [reference])}
      data-status={status}
      data-testid={dataTestId}
    />
  );
};
